import { writable, type Writable } from 'svelte/store'

export interface PersistentStoreOptions<T> {
  key: string
  initial: T
  validate?: (value: unknown) => value is T
}

export function persistentStore<T>(opts: PersistentStoreOptions<T>): Writable<T> {
  const { key, initial, validate } = opts

  const stored = readJson(key)
  const seed = stored !== undefined && (!validate || validate(stored)) ? (stored as T) : initial

  const store = writable<T>(seed)

  store.subscribe((value) => {
    writeJson(key, value)
  })

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key !== key) return
      if (event.newValue === null) {
        store.set(initial)
        return
      }
      try {
        const parsed: unknown = JSON.parse(event.newValue)
        if (!validate || validate(parsed)) {
          store.set(parsed as T)
        }
      } catch {
        // Ignore malformed cross-tab writes.
      }
    })
  }

  return store
}

function readJson(key: string): unknown {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return undefined
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota, disabled storage, etc. — degrade silently to in-memory only.
  }
}
